import React, { useCallback, useEffect, useState } from 'react';
import { ampli } from '@/ampli';
import { useTransactionValues } from '@/hooks/use-transaction-values';
import log from '@/lib/logger';
import {
  startCharge,
  startManualFieldsCharge,
  stopCharge,
} from '@/modules/charge-center/grid-connections/api/connectors/services';
import {
  remoteStartTransactionError,
  remoteTransactionDetailEnum,
} from '@/modules/charge-center/grid-connections/api/connectors/types';
import { updateConnector } from '@/services/connectors';
import { isAxiosError } from 'axios';
import ErrorBoundary from 'components/ErrorBoundary';
import { useAppDispatch, useAppSelector } from 'features';
import { getChargePointByIdSelector } from 'features/ChargePoints';
import { fetchVehicles, unselectVehicles } from 'features/Vehicles';
import { shallowEqual } from 'react-redux';
import { toast } from 'sonner';
import { ConnectorStatusEnum, type Vehicle } from 'types/swagger';

import ConnectorAvailable from './ConnectorAvailable';
import ConnectorCharging from './ConnectorCharging';
import ConnectorFinishing from './ConnectorFinishing';
import ConnectorPreparing from './ConnectorPreparing';

const ConnectorDetails: React.FC = () => {
  const connector = useAppSelector((state) => state.connectors.selectedConnector);
  const dispatch = useAppDispatch();
  const chargePoint = useAppSelector(
    (state) => (connector ? getChargePointByIdSelector(state, connector.charge_point) : null),
    shallowEqual
  );
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [odometerValue, setOdometerValue] = useState('');
  const [socLimit, setSocLimit] = useState(connector?.final_soc || 100);
  const [prevSocLimit, setPrevSocLimit] = useState(socLimit);
  const [isSocLimitChanged, setIsSocLimitChanged] = useState(false);
  const [startingCharge, setStartingCharge] = useState(false);
  const [stoppingCharge, setStoppingCharge] = useState(false);
  const transactionValues = useTransactionValues(connector);
  const status = connector?.latest_status_notification
    ? (connector?.latest_status_notification.status as unknown as ConnectorStatusEnum)
    : ConnectorStatusEnum.Available;
  const handleSetSocLimit = (newSocLimit: number) => {
    setSocLimit(newSocLimit);
  };

  const handleSetSelectedVehicle = (newSelectedVehicle: Vehicle) => {
    setSelectedVehicle(newSelectedVehicle);
  };

  const handleSetOdometerValue = (newOdometerValue: string) => {
    setOdometerValue(newOdometerValue);
  };

  const handleManualStartCharge = useCallback(async () => {
    if (!connector?.id || !selectedVehicle || !odometerValue) return;
    setStartingCharge(true);
    ampli.chargeStartButtonClicked();
    try {
      await updateConnector(connector.id, { final_soc: socLimit });
    } catch (error) {
      log.error('Error updating final SoC', error);
    }
    try {
      const response = await startManualFieldsCharge(
        connector.id,
        selectedVehicle.number_plate,
        odometerValue
      );
      if (response.data.detail === remoteTransactionDetailEnum.Enum['Accepted']) {
        toast.warning('Iniciando carga...');
        return;
      }
      if (response.data.detail === remoteTransactionDetailEnum.Enum['Rejected']) {
        toast.error('La acción fue denegada.');
        return;
      }
      if (response.data.detail === remoteTransactionDetailEnum.Enum['Failed']) {
        toast.error('No hay conexión con el cargador.');
        return;
      }
      toast.error('Ocurrió un error inesperado.');
      return;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 504) {
          toast.error('El cargador no responde.');
          return;
        }
        if (
          error.response?.data ===
          'Usuario no tiene autorización para inciar transacciones de carga'
        ) {
          toast.error('Usuario no tiene autorización para iniciar transacciones de carga');
          return;
        }
      }
      log.error('Unexpected error starting manual charge', error);
      toast.error('Ocurrió un error inesperado.');
    } finally {
      setStartingCharge(false);
    }
  }, [connector?.id, socLimit, odometerValue, selectedVehicle]);

  const handleStartCharge = useCallback(async () => {
    if (!connector?.id) return;
    setStartingCharge(true);
    ampli.chargeStartButtonClicked();
    try {
      await updateConnector(connector.id, { final_soc: socLimit });
    } catch (error) {
      log.error('Error updating final SoC', error);
    }
    try {
      const startChargeRes = await startCharge(connector.id);
      if (startChargeRes.data.detail === remoteTransactionDetailEnum.Enum['Accepted']) {
        toast.warning('Iniciando carga...');
        return;
      }
      if (startChargeRes.data.detail === remoteTransactionDetailEnum.Enum['Rejected']) {
        toast.error('La acción fue denegada.');
        return;
      }
      if (startChargeRes.data.detail === remoteTransactionDetailEnum.Enum['Failed']) {
        toast.error('No hay conexión con el cargador.');
      }
    } catch (error) {
      if (isAxiosError(error)) {
        const managedError = remoteStartTransactionError.safeParse(error.response?.data);
        if (managedError.success) {
          const title = managedError.data.errors.title;
          toast.error(title);
          return;
        }
      }
      log.error('Unexpected error starting charge', error);
      toast.error('Ocurrió un error inesperado.');
    } finally {
      setStartingCharge(false);
    }
  }, [connector?.id, socLimit]);

  const handleStopCharge = useCallback(async () => {
    if (!connector?.id) return;
    setStoppingCharge(true);
    try {
      const stopResponse = await stopCharge(connector.id);
      if (stopResponse.data.detail === remoteTransactionDetailEnum.Enum['Failed']) {
        toast.error('No hay conexión con el cargador.');
        return;
      }
      if (stopResponse.data.detail === remoteTransactionDetailEnum.Enum['Rejected']) {
        toast.error('La acción fue denegada.');
        return;
      }
      if (stopResponse.data.detail === remoteTransactionDetailEnum.Enum['Accepted']) {
        toast.warning('Deteniendo carga...');
      }
    } catch (error) {
      log.error('Unexpected error stopping charge', error);
      toast.error('Ocurrió un error inesperado.');
    } finally {
      setStoppingCharge(false);
    }
  }, [connector?.id]);

  const handleUpdateCharge = useCallback(() => {
    if (!connector) return;
    setIsSocLimitChanged(true);

    updateConnector(connector.id, { final_soc: socLimit })
      .then((_response) => {
        setIsSocLimitChanged(false);
        setPrevSocLimit(socLimit);
        toast.success('SoC máximo actualizado exitosamente.');
      })
      .catch((e) => {
        log.error(e);
        setIsSocLimitChanged(false);
        return toast.error('Error al actualizar el SoC máximo.');
      });
  }, [connector, socLimit]);

  const getDetailsByStatus = useCallback(() => {
    if (!connector) return <span>Hubo un error al obtener el conector</span>;

    if (status === ConnectorStatusEnum.Charging)
      return (
        <ConnectorCharging
          connector={connector}
          isLoading={startingCharge || isSocLimitChanged}
          socLimit={socLimit}
          setSocLimit={handleSetSocLimit}
          prevSocLimit={prevSocLimit}
          handleUpdateCharge={handleUpdateCharge}
          handleToggleCharge={handleStopCharge}
          {...transactionValues}
        />
      );

    if (status === ConnectorStatusEnum.Finishing)
      return (
        <ConnectorFinishing
          connector={connector}
          isLoading={stoppingCharge || isSocLimitChanged}
          selectedVehicle={selectedVehicle}
          setSelectedVehicle={handleSetSelectedVehicle}
          odometerValue={odometerValue}
          setOdometerValue={handleSetOdometerValue}
          socLimit={socLimit}
          setSocLimit={handleSetSocLimit}
          prevSocLimit={prevSocLimit}
          handleUpdateCharge={handleUpdateCharge}
          handleToggleCharge={
            !chargePoint?.fill_manual_fields ? handleStartCharge : handleManualStartCharge
          }
          {...transactionValues}
        />
      );

    if (status === ConnectorStatusEnum.Preparing)
      return (
        <ConnectorPreparing
          connector={connector}
          status={status}
          isLoading={stoppingCharge || isSocLimitChanged}
          selectedVehicle={selectedVehicle}
          setSelectedVehicle={handleSetSelectedVehicle}
          odometerValue={odometerValue}
          setOdometerValue={handleSetOdometerValue}
          socLimit={socLimit}
          setSocLimit={handleSetSocLimit}
          prevSocLimit={prevSocLimit}
          handleUpdateCharge={handleUpdateCharge}
          handleToggleCharge={
            !chargePoint?.fill_manual_fields ? handleStartCharge : handleManualStartCharge
          }
          batterySoc={transactionValues.batterySoc}
        />
      );

    return (
      <ConnectorAvailable
        connector={connector}
        status={status}
        isLoading={startingCharge || stoppingCharge || isSocLimitChanged}
        selectedVehicle={selectedVehicle}
        setSelectedVehicle={handleSetSelectedVehicle}
        odometerValue={odometerValue}
        setOdometerValue={handleSetOdometerValue}
        socLimit={socLimit}
        setSocLimit={handleSetSocLimit}
        prevSocLimit={prevSocLimit}
        handleUpdateCharge={handleUpdateCharge}
        handleToggleCharge={
          !chargePoint?.fill_manual_fields ? handleStartCharge : handleManualStartCharge
        }
      />
    );
  }, [
    transactionValues,
    status,
    connector,
    stoppingCharge,
    startingCharge,
    isSocLimitChanged,
    selectedVehicle,
    odometerValue,
    socLimit,
    prevSocLimit,
    chargePoint?.fill_manual_fields,
    handleUpdateCharge,
    handleStopCharge,
    handleStartCharge,
    handleManualStartCharge,
  ]);

  useEffect(() => {
    if (chargePoint?.fill_manual_fields) void dispatch(fetchVehicles());
  }, [dispatch, chargePoint]);

  useEffect(() => {
    if (socLimit > 100) setSocLimit(100);
    if (socLimit < 0) setSocLimit(0);
  }, [socLimit]);

  useEffect(() => {
    return () => {
      dispatch(unselectVehicles());
    };
  }, [dispatch, status]);

  return <ErrorBoundary>{getDetailsByStatus()}</ErrorBoundary>;
};

export default ConnectorDetails;
